import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import {
  Box,
  Button2,
  PlaceholderNotification,
  Text,
  textStyles,
  theme,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/EmptyList';

TEXT.DEFINE({
  /** Empty state description text */
  emptyListCopy: 'New activity on your account will show here.',
  /** Empty state title */
  emptyListTitle: 'No Updates Yet',
  /** Empty state call-to-action button (empty by default) */
  emptyListCta: '',
});

type EmptyListProps = {
  onPress?: () => void;
  shouldRender: boolean;
  text?: {
    emptyListCopy?: string;
    emptyListTitle?: string;
    emptyListCta?: string;
  };
};

const crossFadeWithDelay = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
  },
  transition: {
    duration: 0.3,
    ease: theme.transitions.curves.primary,
    delay: 0.2,
  },
};

const EmptyContainer = styled(motion.div)`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`;

const EmptyContent = styled.div`
  padding: 0 ${theme.spaces[12]};
  text-align: center;
`;

const EmptyCopy = styled.p`
  ${textStyles.body}
  color: ${theme.colors.text.tertiary};
  margin-top: ${theme.spaces[12]};
`;

export const EmptyList = ({
  onPress,
  shouldRender,
  text = TEXT,
}: EmptyListProps) => {
  const { emptyListCopy, emptyListTitle, emptyListCta } = text;
  return (
    <AnimatePresence>
      {shouldRender && (
        <EmptyContainer {...crossFadeWithDelay}>
          <EmptyContent key="notifications-list-empty">
            <PlaceholderNotification />
            <Text variant="header">{emptyListTitle}</Text>
            {emptyListCopy && <EmptyCopy>{emptyListCopy}</EmptyCopy>}
            {onPress && (
              <Box paddingTop={24} style={{ pointerEvents: 'all' }}>
                <Button2
                  data-testid="notifications-list-empty-cta"
                  onPress={onPress}
                >
                  {emptyListCta}
                </Button2>
              </Box>
            )}
          </EmptyContent>
        </EmptyContainer>
      )}
    </AnimatePresence>
  );
};
